import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";
import { HeaderCount } from "../HeaderCount";

export type CardSurface = "plain" | "raised" | "bordered" | "inset";

const surfaces: Record<CardSurface, string> = {
  plain: "",
  raised: "shadow-card",
  bordered: "border border-border",
  inset: "bg-fill",
};

// Card hover uses tint/elevation/ring only so interactive cards never shift
// layout or compete with dense app surfaces.
export const cardInteractiveMotion =
  "transition-[background-color,box-shadow] duration-base ease-settle hover:bg-fill hover:shadow-card hover:ring-1 hover:ring-border active:shadow-card motion-reduce:transition-none";

const interactiveAffordance = `cursor-pointer text-left ${cardInteractiveMotion}`;

type CardOwnProps<TAs extends ElementType> = {
  surface?: CardSurface;
  interactive?: boolean;
  as?: TAs;
  children?: ReactNode;
};

export type CardProps<TAs extends ElementType = "div"> = CardOwnProps<TAs> &
  Omit<ComponentPropsWithoutRef<TAs>, keyof CardOwnProps<TAs>>;

export function Card<TAs extends ElementType = "div">({
  surface,
  interactive = false,
  as,
  className,
  children,
  ...rest
}: CardProps<TAs>) {
  const Component = (as ?? "div") as ElementType;
  const effectiveSurface = surface ?? (interactive ? "raised" : "plain");

  return (
    <Component
      className={cn(
        "bg-surface text-text rounded-xl",
        surfaces[effectiveSurface],
        interactive && interactiveAffordance,
        interactive && focusRing,
        className,
      )}
      {...(Component === "button" ? { type: "button" as const } : {})}
      {...rest}
    >
      {children}
    </Component>
  );
}

export type CardHeaderProps = {
  title: ReactNode;
  titleAs?: "h2" | "h3" | "h4" | "span";
  eyebrow?: ReactNode;
  count?: ReactNode;
  actions?: ReactNode;
  className?: string;
};

export type CardBodyProps = {
  className?: string;
  children?: ReactNode;
  flush?: boolean;
};

export type CardFooterProps = { className?: string; children?: ReactNode };

export function CardHeader({
  title,
  titleAs: Title = "span",
  eyebrow,
  count,
  actions,
  className,
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 px-5 pb-2 pt-5",
        className,
      )}
    >
      <div className="flex flex-col gap-0.5">
        {eyebrow != null && (
          <span className="text-label text-accent">{eyebrow}</span>
        )}
        <div className="flex items-center gap-2">
          <Title className="text-heading text-text">{title}</Title>
          {count != null && <HeaderCount>{count}</HeaderCount>}
        </div>
      </div>
      {actions != null && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export function CardBody({
  className,
  children,
  flush = false,
}: CardBodyProps) {
  return (
    <div
      className={cn(flush ? "overflow-x-auto pb-0" : "px-5 py-2", className)}
    >
      {children}
    </div>
  );
}

export function CardFooter({ className, children }: CardFooterProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 px-5 pb-5 pt-3",
        className,
      )}
    >
      {children}
    </div>
  );
}
